import * as React from 'react';

import { domToReact, htmlToDOM } from 'html-react-parser';
import { Popover, PopoverInteractionKind } from '@blueprintjs/core';
import Menu from '../Menu';
import MenuItem from '../MenuItem';

export interface IRenderLinkProps {
    /* DOM to parse */
    dom: string;
    /** Click event to bind to A tag */
    onClick?: (event) => void;
    /** Unsafe config */
    config?: any;
    componentMapper?: (maybeComponent: string, maybeContent: string, maybeConfig) => React.Component;
}

/**
 * Renders DOM string to clickable link.
 */
const RenderLink: React.FunctionComponent<IRenderLinkProps> = (props) => {

    const parserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'a' && !!domNode.next && !!domNode.next.children) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, ...rest } = domNode.attribs;

                    return (
                        <Popover
                            enforceFocus={true}
                            interactionKind={PopoverInteractionKind.CLICK}
                            position="bottom-left"
                            usePortal={true}
                            minimal={true}
                            autoFocus={false}
                            canEscapeKeyClose={true}
                            modifiers={{
                                offset: {
                                    offset: '0, 0',
                                },
                                flip: {
                                    enabled: false,
                                },
                            }}
                            className="dropper"
                            portalClassName={'site-menu-portal'}
                            popoverClassName={'site-menu-popover'}
                            content={(
                                <Menu>
                                    {domToReact(domNode.next.children, parserSubOptions)}
                                </Menu>
                            )}
                            target={(
                                <a
                                    {...rest}
                                    className={className}
                                    suppressHydrationWarning={true}
                                    tabIndex={0}
                                >
                                    {inner}
                                </a>
                            )}
                            transitionDuration={0}
                            hoverOpenDelay={0}
                            hoverCloseDelay={100}
                        />
                    );
                }
                if (domNode.name === 'a' && (!domNode.next || !domNode.next.children)) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <a
                            {...rest}
                            className={className}
                            onClick={props.onClick}
                        >
                            {domToReact(domNode.children)}
                        </a>
                    );
                }
            }
            if (props.componentMapper && domNode.attribs && domNode.attribs['data-subcomponent']) {
                const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                const maybeComponent = domNode.attribs['data-subcomponent'];
                const maybeContent = domNode.children ?? '';
                const { class: className, ...rest } = domNode.attribs;
                const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                if (mappedComponent) {
                    return (
                        <domNode.name className={className} {...rest}>
                            {mappedComponent}
                        </domNode.name>
                    );
                }
            }
            if (props.componentMapper && domNode.attribs && domNode.attribs['data-component']) {
                const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                const maybeComponent = domNode.attribs['data-component'];
                const maybeContent = domNode.children ?? '';
                const { class: className, ...rest } = domNode.attribs;
                const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                if (mappedComponent) {
                    return (
                        <domNode.name className={className} {...rest}>
                            {mappedComponent}
                        </domNode.name>
                    );
                }
            }
            return false;
        },
    };

    const parserSubOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'li') {
                    return (
                        <React.Fragment>
                            {domToReact(domNode.children, parserSubOptions)}
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <MenuItem
                            {...rest}
                            className={className}
                            text={domToReact(domNode.children, parserOptions)}
                        >
                            {domToReact(domNode.next.children, parserSubOptions)}
                        </MenuItem>
                    );
                }

                if (domNode.name === 'a' && !domNode.next) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <MenuItem
                            {...rest}
                            className={className}
                            text={domToReact(domNode.children, parserOptions)}
                        />
                    );
                }

                if (domNode.name === 'ul') {
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    // Main output

    const domTree = htmlToDOM(props.dom.replace(/>\s+</g, '><'));

    return (
        <React.Fragment>
            {domToReact(domTree, parserOptions)}
        </React.Fragment>
    );
};

RenderLink.displayName = 'RenderLink';

export default RenderLink;
